import { useEffect, useRef, useState } from 'react'

/**
 * Returns true for `holdMs` after each NEW value of `trigger`. Lets a transient
 * timestamp-shaped signal (e.g. RTK Query mutation `fulfilledTimeStamp`) drive a
 * post-event UI hold.
 *
 * Behavior:
 *   - mount with undefined or any current value: output stays false (no stale hold)
 *   - `trigger` changes to a new value: output flips true and hold timer starts
 *   - after `holdMs`: output flips false
 *   - another new value arrives while held: timer resets to the new event
 *   - unmount: any pending timer is cleared
 *
 * Designed for "skeleton over freshly-recomputed amounts" UX where the skeleton
 * should fire only on success (mutation fulfillment), never during in-flight or
 * on rejected mutations — unchanged amounts shouldn't render a loading state.
 */
export const useHoldAfter = (trigger: number | undefined, holdMs: number): boolean => {
  const [held, setHeld] = useState(false)
  const prevTriggerRef = useRef<number | undefined>(trigger)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // trigger went away (mutation cache reset / cleared) → drop any active hold so
    // consumers don't get stuck visible. Reset prevTriggerRef too so a future fresh
    // value is treated as a new trigger, not a "matches prev" no-op.
    if (trigger === undefined) {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      prevTriggerRef.current = undefined
      setHeld(false)
      return
    }
    if (trigger === prevTriggerRef.current) {
      return
    }
    prevTriggerRef.current = trigger
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
    }
    setHeld(true)
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      setHeld(false)
    }, holdMs)
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [trigger, holdMs])

  return held
}
